/* eslint-disable react-hooks/set-state-in-effect */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Card, CardContent, CardHeader, CardTitle,
} from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../../components/ui/table';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Checkbox } from '../../../components/ui/checkbox';
import { ScrollArea } from '../../../components/ui/scroll-area';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import {
    Building2, Plus, Mail, Trash2, RefreshCw, Loader2, AlertCircle, CheckCircle2,
    Pencil, Send, Copy, ExternalLink, Network,
} from 'lucide-react';
import { formatDate } from '../../../lib/utils';

const API_URL = '';

const EMPTY_CREATE_FORM = {
    name: '',
    manager_first_name: '',
    manager_last_name: '',
    manager_email: '',
    manager_phone: '',
    agency_ids: [],
};

export default function SuperAdminGroupsTab({ token }) {
    const [loading, setLoading] = useState(true);
    const [groups, setGroups] = useState([]);
    const [availableAgencies, setAvailableAgencies] = useState([]);
    const [availMeta, setAvailMeta] = useState({ total_count: 0, taken_count: 0 });

    // Create dialog
    const [createOpen, setCreateOpen] = useState(false);
    const [createForm, setCreateForm] = useState(EMPTY_CREATE_FORM);
    const [createSubmitting, setCreateSubmitting] = useState(false);
    const [agencySearch, setAgencySearch] = useState('');
    const [createResult, setCreateResult] = useState(null);

    // Edit dialog
    const [editOpen, setEditOpen] = useState(false);
    const [editGroup, setEditGroup] = useState(null);
    const [editForm, setEditForm] = useState({ name: '', agency_ids: [] });
    const [editSubmitting, setEditSubmitting] = useState(false);

    // Delete dialog
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteGroup, setDeleteGroup] = useState(null);

    const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

    const loadAll = async () => {
        setLoading(true);
        try {
            const [groupsRes, availRes] = await Promise.all([
                axios.get(`${API_URL}/api/super-admin/groups`, { headers: authHeaders }),
                axios.get(`${API_URL}/api/super-admin/groups/available-agencies`, { headers: authHeaders }),
            ]);
            setGroups(groupsRes.data || []);
            setAvailableAgencies(availRes.data?.available || []);
            setAvailMeta({
                total_count: availRes.data?.total_count || 0,
                taken_count: availRes.data?.taken_count || 0,
            });
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors du chargement des groupes');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (token) loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const filteredAgencies = useMemo(() => {
        const q = agencySearch.trim().toLowerCase();
        if (!q) return availableAgencies;
        return availableAgencies.filter((a) =>
            (a.name || '').toLowerCase().includes(q) || (a.city || '').toLowerCase().includes(q),
        );
    }, [availableAgencies, agencySearch]);

    const toggleAgency = (agencyId, isCreate = true) => {
        if (isCreate) {
            setCreateForm((prev) => {
                const set = new Set(prev.agency_ids);
                if (set.has(agencyId)) set.delete(agencyId);
                else set.add(agencyId);
                return { ...prev, agency_ids: Array.from(set) };
            });
        } else {
            setEditForm((prev) => {
                const set = new Set(prev.agency_ids);
                if (set.has(agencyId)) set.delete(agencyId);
                else set.add(agencyId);
                return { ...prev, agency_ids: Array.from(set) };
            });
        }
    };

    const submitCreate = async () => {
        if (!createForm.name.trim() || !createForm.manager_email.trim() || !createForm.manager_first_name.trim() || !createForm.manager_last_name.trim()) {
            toast.error('Nom du groupe et infos du dirigeant requis');
            return;
        }
        if (createForm.agency_ids.length === 0) {
            toast.error('Sélectionnez au moins une agence à rattacher');
            return;
        }
        setCreateSubmitting(true);
        try {
            const res = await axios.post(
                `${API_URL}/api/super-admin/groups`,
                createForm,
                { headers: authHeaders },
            );
            setCreateResult(res.data);
            await loadAll();
            if (res.data.email_sent) {
                toast.success(`Groupe créé · Email d'activation envoyé à ${createForm.manager_email}`);
            } else {
                toast.warning(`Groupe créé, mais email non envoyé. ${res.data.email_error || ''}`);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors de la création');
        } finally {
            setCreateSubmitting(false);
        }
    };

    const openEdit = (group) => {
        setEditGroup(group);
        setEditForm({
            name: group.name || '',
            agency_ids: (group.agencies || []).map((a) => a.id),
        });
        setEditOpen(true);
    };

    const submitEdit = async () => {
        if (!editGroup) return;
        setEditSubmitting(true);
        try {
            await axios.patch(
                `${API_URL}/api/super-admin/groups/${editGroup.id}`,
                {
                    name: editForm.name,
                    agency_ids: editForm.agency_ids,
                },
                { headers: authHeaders },
            );
            toast.success('Groupe mis à jour');
            setEditOpen(false);
            setEditGroup(null);
            await loadAll();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors de la mise à jour');
        } finally {
            setEditSubmitting(false);
        }
    };

    const submitDelete = async () => {
        if (!deleteGroup) return;
        try {
            await axios.delete(`${API_URL}/api/super-admin/groups/${deleteGroup.id}`, { headers: authHeaders });
            toast.success('Groupe supprimé');
            setDeleteOpen(false);
            setDeleteGroup(null);
            await loadAll();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors de la suppression');
        }
    };

    const resendInvite = async (groupId) => {
        try {
            const res = await axios.post(
                `${API_URL}/api/super-admin/groups/${groupId}/resend-invite`,
                {},
                { headers: authHeaders },
            );
            if (res.data.success) {
                toast.success("Email d'activation renvoyé");
            } else {
                toast.warning(`Renvoi : ${res.data.error || 'Échec'}`);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur lors du renvoi");
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(
            () => toast.success('Lien copié'),
            () => toast.error('Copie impossible'),
        );
    };

    const editAvailableAgencies = useMemo(() => {
        // For edit dialog: show available + already in this group
        if (!editGroup) return [];
        const own = editGroup.agencies || [];
        const ownIds = new Set(own.map((a) => a.id));
        const extras = availableAgencies.filter((a) => !ownIds.has(a.id));
        return [...own, ...extras];
    }, [availableAgencies, editGroup]);

    return (
        <div className="space-y-4" data-testid="superadmin-groups-tab">
            {/* Header card */}
            <Card className="bg-gradient-to-br from-[#681793]/10 to-[#FF914D]/5 border-[#681793]/30">
                <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center">
                            <Network className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-white">Groupes Multi-Agences</h2>
                            <p className="text-sm text-muted-foreground">
                                {groups.length} groupe{groups.length > 1 ? 's' : ''} · {availMeta.taken_count}/{availMeta.total_count} agences rattachées · {availableAgencies.length} disponible{availableAgencies.length > 1 ? 's' : ''}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={loadAll} disabled={loading} className="border-border" data-testid="refresh-groups-button">
                            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                            Actualiser
                        </Button>
                        <Button
                            onClick={() => { setCreateForm(EMPTY_CREATE_FORM); setCreateResult(null); setCreateOpen(true); }}
                            className="bg-gradient-to-r from-[#681793] to-[#FF914D] hover:from-[#581180] hover:to-[#e6803c] text-white"
                            data-testid="create-group-button"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Créer un groupe
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Groups table */}
            <Card className="bg-gray-800 border-border">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Building2 className="w-5 h-5" />
                        Groupes clients ({groups.length})
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="py-12 flex items-center justify-center text-muted-foreground">
                            <Loader2 className="w-5 h-5 animate-spin mr-2" />
                            Chargement…
                        </div>
                    ) : groups.length === 0 ? (
                        <div className="py-12 text-center text-muted-foreground" data-testid="empty-groups">
                            <Network className="w-10 h-10 mx-auto mb-3 opacity-40" />
                            <p>Aucun groupe multi-agences pour le moment.</p>
                            <p className="text-xs mt-1">Créez votre 1er client réseau via le bouton « Créer un groupe ».</p>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow className="border-border">
                                    <TableHead className="text-muted-foreground">Groupe</TableHead>
                                    <TableHead className="text-muted-foreground">Dirigeant</TableHead>
                                    <TableHead className="text-muted-foreground">Agences</TableHead>
                                    <TableHead className="text-muted-foreground">Mandats actifs</TableHead>
                                    <TableHead className="text-muted-foreground">Activation</TableHead>
                                    <TableHead className="text-muted-foreground">Créé le</TableHead>
                                    <TableHead className="text-muted-foreground text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {groups.map((g) => {
                                    const isPending = g.manager?.activation_status === 'pending_setup';
                                    return (
                                        <TableRow key={g.id} className="border-border" data-testid={`group-row-${g.id}`}>
                                            <TableCell className="font-medium">{g.name}</TableCell>
                                            <TableCell>
                                                {g.manager ? (
                                                    <div>
                                                        <div className="text-sm text-white">{g.manager.first_name} {g.manager.last_name}</div>
                                                        <div className="text-xs text-muted-foreground">{g.manager.email}</div>
                                                    </div>
                                                ) : (
                                                    <span className="text-muted-foreground text-sm">—</span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="outline">{g.agency_count}</Badge>
                                            </TableCell>
                                            <TableCell className="font-semibold text-emerald-400">{g.active_mandates}</TableCell>
                                            <TableCell>
                                                {isPending ? (
                                                    <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30 hover:bg-amber-500/20">
                                                        <AlertCircle className="w-3 h-3 mr-1" />
                                                        En attente d&apos;activation
                                                    </Badge>
                                                ) : (
                                                    <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/20">
                                                        <CheckCircle2 className="w-3 h-3 mr-1" />
                                                        Actif
                                                    </Badge>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-muted-foreground text-sm">{formatDate(g.created_at)}</TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex items-center justify-end gap-1">
                                                    {isPending && (
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={() => resendInvite(g.id)}
                                                            title="Renvoyer l'email d'activation"
                                                            data-testid={`resend-invite-${g.id}`}
                                                        >
                                                            <Send className="w-4 h-4" />
                                                        </Button>
                                                    )}
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={() => openEdit(g)}
                                                        title="Éditer"
                                                        data-testid={`edit-group-${g.id}`}
                                                    >
                                                        <Pencil className="w-4 h-4" />
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={() => { setDeleteGroup(g); setDeleteOpen(true); }}
                                                        title="Supprimer"
                                                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                                        data-testid={`delete-group-${g.id}`}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* ----- CREATE DIALOG ----- */}
            <Dialog open={createOpen} onOpenChange={(o) => { setCreateOpen(o); if (!o) setCreateResult(null); }}>
                <DialogContent className="max-w-2xl bg-gray-900 border-border text-white" data-testid="create-group-dialog">
                    {!createResult ? (
                        <>
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2">
                                    <Network className="w-5 h-5 text-[#FF914D]" />
                                    Nouveau groupe multi-agences
                                </DialogTitle>
                                <DialogDescription className="text-muted-foreground">
                                    Le dirigeant recevra un email Homly avec un lien sécurisé pour activer son espace et créer son mot de passe.
                                </DialogDescription>
                            </DialogHeader>

                            <div className="space-y-4 py-2">
                                <div>
                                    <Label htmlFor="group-name">Nom du groupe</Label>
                                    <Input
                                        id="group-name"
                                        placeholder="ex. Groupe Boschi Immobilier"
                                        value={createForm.name}
                                        onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                                        className="mt-1.5 bg-gray-800 border-border"
                                        data-testid="input-group-name"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label htmlFor="mgr-fn">Prénom du dirigeant</Label>
                                        <Input
                                            id="mgr-fn"
                                            value={createForm.manager_first_name}
                                            onChange={(e) => setCreateForm({ ...createForm, manager_first_name: e.target.value })}
                                            className="mt-1.5 bg-gray-800 border-border"
                                            data-testid="input-manager-firstname"
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="mgr-ln">Nom du dirigeant</Label>
                                        <Input
                                            id="mgr-ln"
                                            value={createForm.manager_last_name}
                                            onChange={(e) => setCreateForm({ ...createForm, manager_last_name: e.target.value })}
                                            className="mt-1.5 bg-gray-800 border-border"
                                            data-testid="input-manager-lastname"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label htmlFor="mgr-email">Email du dirigeant</Label>
                                        <Input
                                            id="mgr-email"
                                            type="email"
                                            value={createForm.manager_email}
                                            onChange={(e) => setCreateForm({ ...createForm, manager_email: e.target.value })}
                                            className="mt-1.5 bg-gray-800 border-border"
                                            placeholder="dirigeant@groupe.com"
                                            data-testid="input-manager-email"
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="mgr-phone">Téléphone (optionnel)</Label>
                                        <Input
                                            id="mgr-phone"
                                            value={createForm.manager_phone}
                                            onChange={(e) => setCreateForm({ ...createForm, manager_phone: e.target.value })}
                                            className="mt-1.5 bg-gray-800 border-border"
                                            placeholder="+33 ..."
                                            data-testid="input-manager-phone"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <div className="flex items-center justify-between mb-1.5">
                                        <Label>Agences à rattacher · {createForm.agency_ids.length} sélectionnée{createForm.agency_ids.length > 1 ? 's' : ''}</Label>
                                        <Input
                                            placeholder="Rechercher…"
                                            value={agencySearch}
                                            onChange={(e) => setAgencySearch(e.target.value)}
                                            className="w-48 h-8 bg-gray-800 border-border text-sm"
                                            data-testid="agency-search"
                                        />
                                    </div>
                                    <div className="border border-border rounded-lg bg-gray-800/60">
                                        <ScrollArea className="h-56">
                                            {filteredAgencies.length === 0 ? (
                                                <div className="p-4 text-center text-sm text-muted-foreground">
                                                    Aucune agence disponible. {availMeta.taken_count > 0 && `(${availMeta.taken_count} déjà rattachées à un autre groupe)`}
                                                </div>
                                            ) : (
                                                <div className="p-2 space-y-1">
                                                    {filteredAgencies.map((a) => {
                                                        const checked = createForm.agency_ids.includes(a.id);
                                                        return (
                                                            <label
                                                                key={a.id}
                                                                className={`flex items-center gap-3 p-2 rounded-md cursor-pointer transition ${checked ? 'bg-[#681793]/15 border border-[#681793]/40' : 'hover:bg-gray-700/50 border border-transparent'}`}
                                                                data-testid={`agency-row-${a.id}`}
                                                            >
                                                                <Checkbox
                                                                    checked={checked}
                                                                    onCheckedChange={() => toggleAgency(a.id, true)}
                                                                />
                                                                <div className="flex-1">
                                                                    <div className="text-sm text-white">{a.name}</div>
                                                                    <div className="text-xs text-muted-foreground">{a.city || '—'}{a.email ? ` · ${a.email}` : ''}</div>
                                                                </div>
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </ScrollArea>
                                    </div>
                                </div>
                            </div>

                            <DialogFooter>
                                <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={createSubmitting} className="border-border">
                                    Annuler
                                </Button>
                                <Button
                                    onClick={submitCreate}
                                    disabled={createSubmitting}
                                    className="bg-gradient-to-r from-[#681793] to-[#FF914D] hover:from-[#581180] hover:to-[#e6803c] text-white"
                                    data-testid="submit-create-group"
                                >
                                    {createSubmitting ? (
                                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Création…</>
                                    ) : (
                                        <><Mail className="w-4 h-4 mr-2" /> Créer &amp; Envoyer l&apos;invitation</>
                                    )}
                                </Button>
                            </DialogFooter>
                        </>
                    ) : (
                        <>
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2 text-emerald-400">
                                    <CheckCircle2 className="w-5 h-5" />
                                    Groupe créé avec succès
                                </DialogTitle>
                                <DialogDescription className="text-muted-foreground">
                                    {createResult.email_sent
                                        ? `Email d'activation envoyé à ${createForm.manager_email}.`
                                        : `Groupe créé. ⚠️ L'email n'a pas pu être envoyé automatiquement (${createResult.email_error || 'erreur Resend'}).`}
                                </DialogDescription>
                            </DialogHeader>

                            <div className="space-y-3 py-3">
                                <div className="rounded-lg bg-gray-800 border border-border p-4">
                                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Récapitulatif</p>
                                    <div className="space-y-1.5 text-sm">
                                        <div><span className="text-muted-foreground">Groupe :</span> <span className="text-white font-medium">{createResult.group?.name}</span></div>
                                        <div><span className="text-muted-foreground">Dirigeant :</span> <span className="text-white">{createResult.group?.manager?.first_name} {createResult.group?.manager?.last_name}</span></div>
                                        <div><span className="text-muted-foreground">Email :</span> <span className="text-white">{createResult.group?.manager?.email}</span></div>
                                        <div><span className="text-muted-foreground">Agences rattachées :</span> <span className="text-white">{createResult.group?.agency_count}</span></div>
                                    </div>
                                </div>

                                {!createResult.email_sent && createResult.setup_link_dev && (
                                    <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-4">
                                        <p className="text-sm text-amber-300 font-medium mb-2 flex items-center gap-2">
                                            <AlertCircle className="w-4 h-4" />
                                            Lien d&apos;activation (à transmettre manuellement)
                                        </p>
                                        <div className="flex items-center gap-2">
                                            <code className="flex-1 text-xs bg-gray-900 text-amber-200 p-2 rounded break-all">
                                                {window.location.origin}{createResult.setup_link_dev}
                                            </code>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => copyToClipboard(`${window.location.origin}${createResult.setup_link_dev}`)}
                                                className="border-border"
                                                data-testid="copy-setup-link"
                                            >
                                                <Copy className="w-4 h-4" />
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => window.open(createResult.setup_link_dev, '_blank')}
                                                className="border-border"
                                            >
                                                <ExternalLink className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <DialogFooter>
                                <Button
                                    onClick={() => { setCreateOpen(false); setCreateResult(null); }}
                                    className="bg-gradient-to-r from-[#681793] to-[#FF914D] text-white"
                                >
                                    Fermer
                                </Button>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* ----- EDIT DIALOG ----- */}
            <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogContent className="max-w-2xl bg-gray-900 border-border text-white" data-testid="edit-group-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Pencil className="w-5 h-5 text-[#FF914D]" />
                            Éditer le groupe
                        </DialogTitle>
                        <DialogDescription className="text-muted-foreground">
                            Modifiez le nom et la liste des agences rattachées.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-2">
                        <div>
                            <Label htmlFor="edit-group-name">Nom du groupe</Label>
                            <Input
                                id="edit-group-name"
                                value={editForm.name}
                                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                className="mt-1.5 bg-gray-800 border-border"
                                data-testid="edit-input-group-name"
                            />
                        </div>

                        <div>
                            <Label>Agences rattachées · {editForm.agency_ids.length} sélectionnée{editForm.agency_ids.length > 1 ? 's' : ''}</Label>
                            <div className="mt-1.5 border border-border rounded-lg bg-gray-800/60">
                                <ScrollArea className="h-56">
                                    {editAvailableAgencies.length === 0 ? (
                                        <div className="p-4 text-center text-sm text-muted-foreground">
                                            Aucune agence disponible.
                                        </div>
                                    ) : (
                                        <div className="p-2 space-y-1">
                                            {editAvailableAgencies.map((a) => {
                                                const checked = editForm.agency_ids.includes(a.id);
                                                return (
                                                    <label
                                                        key={a.id}
                                                        className={`flex items-center gap-3 p-2 rounded-md cursor-pointer transition ${checked ? 'bg-[#681793]/15 border border-[#681793]/40' : 'hover:bg-gray-700/50 border border-transparent'}`}
                                                    >
                                                        <Checkbox
                                                            checked={checked}
                                                            onCheckedChange={() => toggleAgency(a.id, false)}
                                                        />
                                                        <div className="flex-1">
                                                            <div className="text-sm text-white">{a.name}</div>
                                                            <div className="text-xs text-muted-foreground">{a.city || '—'}</div>
                                                        </div>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    )}
                                </ScrollArea>
                            </div>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditOpen(false)} className="border-border">
                            Annuler
                        </Button>
                        <Button
                            onClick={submitEdit}
                            disabled={editSubmitting}
                            className="bg-gradient-to-r from-[#681793] to-[#FF914D] text-white"
                            data-testid="submit-edit-group"
                        >
                            {editSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                            Enregistrer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ----- DELETE DIALOG ----- */}
            <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                <DialogContent className="max-w-md bg-gray-900 border-border text-white" data-testid="delete-group-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-400">
                            <Trash2 className="w-5 h-5" />
                            Supprimer le groupe
                        </DialogTitle>
                        <DialogDescription className="text-muted-foreground">
                            Vous êtes sur le point de supprimer <strong className="text-white">{deleteGroup?.name}</strong>.
                            Les agences rattachées ne seront <strong>pas</strong> supprimées. Le dirigeant sera désactivé.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteOpen(false)} className="border-border">
                            Annuler
                        </Button>
                        <Button
                            onClick={submitDelete}
                            className="bg-red-600 hover:bg-red-700 text-white"
                            data-testid="confirm-delete-group"
                        >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Confirmer la suppression
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
